// A tiny typed event bus for what the API fetcher reports to the shell: the step-up dialog
// (AUTH) and upgrade prompts (REL) subscribe to it.
import type { ApiError, ApiRequest } from '@batiflow/client-core';

export interface AppEvents {
  /** 403 STEP_UP_REQUIRED: the shell opens the step-up dialog, then replays `request`. */
  stepUpRequired: { error: ApiError; request: ApiRequest };
  /** 403 PLAN_LIMIT_REACHED: the shell shows an upgrade prompt. */
  planLimitReached: ApiError;
}

export type AppEventName = keyof AppEvents;

export interface EventBus {
  emit<K extends AppEventName>(name: K, payload: AppEvents[K]): void;
  /** Returns the unsubscribe function. */
  on<K extends AppEventName>(name: K, listener: (payload: AppEvents[K]) => void): () => void;
}

export function createEventBus(): EventBus {
  const listeners = new Map<AppEventName, Set<(payload: never) => void>>();
  return {
    emit(name, payload) {
      for (const listener of listeners.get(name) ?? [])
        (listener as (p: typeof payload) => void)(payload);
    },
    on(name, listener) {
      const set = listeners.get(name) ?? new Set();
      set.add(listener);
      listeners.set(name, set);
      return () => {
        set.delete(listener);
      };
    },
  };
}
